import { Component, Inject, OnInit } from '@angular/core';
import { DataQueryInformation } from '../../models/data-query-information';
import { Subscription } from 'rxjs';
import { NestedData } from '../../../../shared-components/virtualized-table/nested-data';
import { LoginService } from '../../../../shared-modules/login/login.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataQueryField } from '../../models/data-query-field.model';
import { DataQueryPreferences } from '../../../../shared-components/user-preferences/data-query-preferences.model';
import { DataQuery } from '../../models/data-query.model';

@Component({
    selector: 'app-insert-field-dialog',
    templateUrl: './insert-field-dialog.component.html',
    styleUrls: [
        './insert-field-dialog.component.css',
        '../../data-query-shared-style.css',
        '../../virtualized-table.css'
    ]
})
export class InsertFieldDialogComponent implements OnInit {

    // Injected Properties
    information: DataQueryInformation;
    isStudentSearch: boolean;
    isCourseSearch: boolean;
    isStaffSearch: boolean;
    callByEform: boolean;
    disabledFields: { [fieldId: string]: boolean } = null;
    preferences = new DataQueryPreferences();
    currentQuery: DataQuery;

    // Properties
    subscriptions: Subscription[] = [];
    tableData: NestedData[] = [];
    fieldSearch = '';
    columns = DataQueryInformation.virtualizedTableColumns;
    V10: boolean;
    filterColumnSql: string[] = [];
    filterColumnFieldName: string[];

    // Class Functions
    constructor(
        private loginService: LoginService,
        private dialogBox: MatDialogRef<InsertFieldDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: any
    ) { }

    // Event Functions
    ngOnInit() {
        // POST: syncs the data
        this.information = this.data.information;
        this.isStudentSearch = this.data.isStudentSearch;
        this.isCourseSearch = this.data.isCourseSearch;
        this.isStaffSearch = this.data.isStaffSearch;
        this.disabledFields = this.data.disabledFields;
        this.callByEform = this.data.callByEform;
        this.preferences = this.data.preferences;
        this.currentQuery = this.data.currentQuery;
        this.V10 = this.loginService.LoginSettings.V10;

        // If this is staff search, we need a column filter for staff_id
        if (this.isStaffSearch) {
            this.filterColumnSql = this.V10 ? ['staff_id'] : null;
            this.filterColumnFieldName = null;
        }

        // If this is course search, we need a column filter for c_id
        else if (this.isCourseSearch) {
            this.filterColumnSql = this.V10 ? ['c_id'] : null;
            this.filterColumnFieldName = null;
        }

        // If this is student search, we need a column filter
        else if (this.isStudentSearch) {
            this.filterColumnSql = this.V10 ? ['student_id'] : null;
            this.filterColumnFieldName = null;
        }

        // If this isn't student search, show everything
        else {
            this.filterColumnSql = null;
            this.filterColumnFieldName = null;
        }

        // Setting up the grid
        this.tableData = this.information.virtualizedTableData({
            fieldSearch: this.fieldSearch,
            V10: this.loginService.LoginSettings.V10,
            allowTab: true,
            click: (event: MouseEvent, nestedData: NestedData) => {
                this.fieldSelect(nestedData.data);
            },
            keydown: (event: KeyboardEvent, nestedData: NestedData) => {
                if (event.key == 'Enter') {
                    this.fieldSelect(nestedData.data);
                }
            },
            filterColumnSQL: this.filterColumnSql,
            filterColumnFieldName: this.filterColumnFieldName,
            disabledFields: this.disabledFields,
            preferences: this.preferences,
            currentQuery: this.currentQuery
        });

        // default expand if only one object is available...
        if (this.information != null && this.information.objects.length == 1) {
            this.expandAll();
        }
    }

    close() {
        // PRE: the close button is clicked
        // POST: closes the dialog box with nothing
        this.dialogBox.close();
    }

    fieldSelect(field: DataQueryField) {
        // PRE: a field is clicked or has entered pressed while focused on it on the table
        // POST: closes the dialog box with the field
        this.dialogBox.close(field);
    }

    // Methods
    applySearch(searchString: string) {
        // POST: applies the search to the available fields
        if (this.fieldSearch != searchString) {
            this.fieldSearch = searchString;
            this.tableData = this.information.virtualizedTableData({
                fieldSearch: this.fieldSearch,
                V10: this.loginService.LoginSettings.V10,
                allowTab: true,
                click: (event: MouseEvent, nestedData: NestedData) => {
                    this.fieldSelect(nestedData.data);
                },
                keydown: (event: KeyboardEvent, nestedData: NestedData) => {
                    if (event.key == 'Enter') {
                        this.fieldSelect(nestedData.data);
                    }
                },
                filterColumnSQL: this.filterColumnSql,
                filterColumnFieldName: this.filterColumnFieldName,
                disabledFields: this.disabledFields
            });
            if (this.fieldSearch) {
                this.expandAll();
            }
        }
    }

    expandAll() {
        // POST: expand all the rows
        // NOTE: we must force change detection because the virtualized table uses OnPush
        const previousTableData = Object.assign([], this.tableData);
        this.tableData = [];
        const expandData = (data: NestedData[]) => {
            data.forEach(datum => {
                datum.showChildren = true;
                if (datum.children) {
                    expandData(datum.children);
                }
            });
        };
        expandData(previousTableData);
        this.tableData = previousTableData;
    }
}
